import {
  FREE_TIER_DAILY_NEW_GRAPHEME_LIMIT,
  FREE_TIER_DAILY_NEW_QUEST_LIMIT,
} from "@-/dev.improve.haniverse.lib.entitlement.free-tier";

export type PremiumBenefit = {
  /** One-line elaboration of what the free tier gives instead. */
  description: string;
  /** `Icon` name for the bullet. */
  icon: "LuBookMarked" | "LuBookOpen" | "LuComponent" | "LuLibrary" | "LuMic" | "MdSchool";
  title: string;
};

/**
 * Every feature premium unlocks, as one list shared by every surface that pitches it.
 *
 * Deliberately built from the gates that actually exist in code, so the pitch cannot drift from
 * the product. Each entry maps to a real check:
 *
 * - Levels / adding characters → `useFreeTierGate` + `getAccessibleLevels`
 * - Daily new characters → `FREE_TIER_DAILY_NEW_GRAPHEME_LIMIT` (the lesson resolver + learn queue)
 * - Daily new map quests → `unlockQuest`
 * - Meaning mnemonics → `resolveMnemonicGateState` (`premium-locked`)
 * - Pronunciation mnemonics → the same gate on the pronunciation tab
 * - Voice study mnemonics → `VoiceStudyFlashcardBack`'s `catalogVisible`
 *
 * Note what is deliberately ABSENT: cloud sync / cross-device save. The settings pitch claims it,
 * but sync is available to every signed-in user without an entitlement check, and offline downloads are ungated
 * too. Listing either here would be a promise the app does not keep.
 */
export const PREMIUM_BENEFITS: readonly PremiumBenefit[] = [
  {
    description: `The free tier unlocks ${FREE_TIER_DAILY_NEW_QUEST_LIMIT} new map mission a day. Premium lets you unlock as many as you want.`,
    icon: "LuBookOpen",
    title: "Unlimited map missions every day",
  },
  {
    description: `The free tier introduces up to ${FREE_TIER_DAILY_NEW_GRAPHEME_LIMIT} new characters a day. Premium lets you learn as many as you want.`,
    icon: "MdSchool",
    title: "Unlimited new characters every day",
  },
  {
    description: "The free tier stops after HSK 1. Premium opens the rest of the curriculum.",
    icon: "LuLibrary",
    title: "Every level unlocked",
  },
  {
    description: "Add any character to your deck, not just the ones introduced through HSK 1.",
    icon: "LuBookOpen",
    title: "Study any character",
  },
  {
    description:
      "The story and component breakdown behind every character, not just the early ones.",
    icon: "LuComponent",
    title: "Mnemonics for every character",
  },
  {
    description: "The pinyin memory phrase that makes a character's reading stick.",
    icon: "LuBookMarked",
    title: "Pronunciation mnemonics",
  },
  {
    description: "Hands-free drills read out the full mnemonic and breakdown for every card.",
    icon: "LuMic",
    title: "Full mnemonics in voice study",
  },
] as const;
